import { graphql } from 'app/generated/gql'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { ReactElement, useEffect, useState } from 'react'

import { ClosedLostForm, type ClosedLostFormData } from 'app/components/ClosedLostForm'
import { LeadStatusTrigger } from 'app/components/contacts/LeadStatusTrigger'
import { InlineAddChip } from 'app/components/shared/InlineAddActionLink'
import { cellChevronClasses, editableCellTrigger } from 'app/components/table/cellInteraction'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import type { ContactJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { gqlMutation } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { getLeadStatusColorMap } from 'lib/colorMappings'
import { isSMCOrg } from 'lib/isSMCOrg'
import { logError } from 'lib/logging'

const UPDATE_LEAD_STATUS_MUTATION = graphql(`
  mutation UpdateContactLeadStatus($id: ID!, $input: UpdateContactInput!) {
    updateContact(id: $id, input: $input) {
      id
      leadStatus
      closedLostReason
      isQualified
    }
  }
`)

// Closed Won is a terminal status the server locks and sets itself; it is never
// offered as a target (mirrors the drawer editor and bulk actions).
const LEAD_STATUS_OPTIONS: ContactJson[`leadStatus`][] = [`Needs Action`, `In Progress`, `Scheduled`, `Closed Lost`]

type LeadStatusCellProps = {
  contact?: ContactJson
  /** Render read-only (permission gate handled by the column). */
  editable?: boolean
  /** `field` draws the drawer's Lead Status control instead of a table cell. */
  variant?: `cell` | `field`
  onSaved?: (contact: ContactJson) => void
}

function StatusDot({ status }: { status?: ContactJson[`leadStatus`] }): ReactElement {
  return (
    <span
      className="mr-1.5 inline-block h-2 w-2 shrink-0 rounded-[2px] align-middle"
      style={{ backgroundColor: getLeadStatusColorMap(status) }}
    />
  )
}

/** Inline lead-status select for the contacts table. Follows the cell grammar:
 *  data at rest, chevron + tint on cell hover, dropdown on click. Picking
 *  Closed Lost opens the ClosedLostForm (reason required) and records the
 *  milestone, mirroring the drawer's ContactInfo flow. */
export function LeadStatusCell({
  contact,
  editable = true,
  variant = `cell`,
  onSaved,
}: LeadStatusCellProps): ReactElement {
  const user = useUser()
  const [current, setCurrent] = useState(contact?.leadStatus)
  useEffect(() => setCurrent(contact?.leadStatus), [contact?.leadStatus])

  if (!editable || !contact?.id || current === `Closed Won`) {
    return (
      <div
        className={
          variant === `field` ? `flex shrink-0 items-center text-body-xs text-subtle` : `flex h-full items-center px-2`
        }
        title={current === `Closed Won` ? `Lead status is locked once it's Closed Won` : undefined}
      >
        {current && <StatusDot status={current} />}
        <span className="truncate">{current}</span>
      </div>
    )
  }

  const saveStatus = async (value: string) => {
    if (!contact.id) return
    const previous = current
    setCurrent(value as ContactJson[`leadStatus`])
    const result = await gqlMutation(UPDATE_LEAD_STATUS_MUTATION, { id: contact.id, input: { leadStatus: value } })
    if (result.error) {
      logError(`Failed to update lead status`, result.error.message)
      notifyError(`Failed to update lead status: ${result.error.message}`)
      setCurrent(previous)
    } else {
      notifySuccess(`Lead status updated`)
      onSaved?.({ ...contact, leadStatus: value as ContactJson[`leadStatus`] })
    }
  }

  const openClosedLostFlow = () => {
    if (!user) return
    showModal(
      <ClosedLostForm
        contact={contact}
        handleSubmit={async (data: ClosedLostFormData) => {
          if (!data?.closedLostReason) return
          const isQualified = isSMCOrg(user.orgId)
            ? ![`Service Not Offered`, `Insurance: State / HMO`, `Office Too Far`].includes(data.closedLostReason)
            : data.disqualified === `false`
          const result = await gqlMutation(UPDATE_LEAD_STATUS_MUTATION, {
            id: contact.id!,
            input: {
              leadStatus: `Closed Lost`,
              closedLostReason: data.closedLostReason,
              isQualified,
              note: data.note,
              ownerId: data.owner?.id,
              ownerName: data.owner?.name,
            },
          })
          if (result.error) {
            logError(`Error closing lead as lost from table`, result.error.message)
            notifyError(`Failed to update lead status: ${result.error.message}`)
            return
          }
          setCurrent(`Closed Lost`)
          notifySuccess(`Save Successful`)
          onSaved?.({ ...contact, leadStatus: `Closed Lost` })
          closeModal()
        }}
        handleCancel={closeModal}
      />,
      { title: `Closed lost`, isFullScreen: false, overflowHidden: false, useRadix: true },
    )
  }

  const pick = (value: string) => {
    if (value === current) return
    if (value === `Closed Lost`) openClosedLostFlow()
    else bg(saveStatus(value))
  }

  return (
    // modal={false} + stopPropagation: same Radix/table constraints as PipelineStageDropdown.
    <DropdownMenu modal={false}>
      {variant === `field` ? (
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <LeadStatusTrigger status={current} data-testid="list-row-lead-status" className="max-w-40" />
        </DropdownMenuTrigger>
      ) : (
        <DropdownMenuTrigger className={editableCellTrigger} onClick={(e) => e.stopPropagation()}>
          {current ? (
            <>
              <StatusDot status={current} />
              <span className="truncate">{current}</span>
              <ChevronDownIcon className={cellChevronClasses} />
            </>
          ) : (
            <InlineAddChip verb="Set status" />
          )}
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="start" className="w-44 bg-surface-raised" onClick={(e) => e.stopPropagation()}>
        {LEAD_STATUS_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option}
            onSelect={() => pick(option!)}
            className={`flex items-center gap-2 text-body-xs ${
              option === current ? `font-semibold text-strong` : `text-default`
            }`}
          >
            <StatusDot status={option} />
            <span className="truncate">{option}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
